"use client";

import { Analytics as DubAnalytics } from "@dub/analytics/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { signIn } from "next-auth/react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Script from "next/script";
import posthog from "posthog-js";
import React, { useState, useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm, useFormContext } from "react-hook-form";
import { Toaster } from "sonner";
import { z } from "zod";

import getStripe from "@calcom/app-store/stripepayment/lib/client";
import { getPremiumPlanPriceValue } from "@calcom/app-store/stripepayment/lib/utils";
import { fetchSignup, isUserAlreadyExistsError, hasCheckoutSession } from "@calcom/features/auth/signup/lib/fetchSignup";
import { getOrgUsernameFromEmail } from "@calcom/features/auth/signup/utils/getOrgUsernameFromEmail";
import { getOrgFullOrigin } from "@calcom/features/ee/organizations/lib/orgDomains";
import ServerTrans from "@calcom/lib/components/ServerTrans";
import {
  APP_NAME,
  URL_PROTOCOL_REGEX,
  IS_CALCOM,
  WEBAPP_URL,
  CLOUDFLARE_SITE_ID,
  WEBSITE_PRIVACY_POLICY_URL,
  WEBSITE_TERMS_URL,
  WEBSITE_URL,
} from "@calcom/lib/constants";
import { isENVDev } from "@calcom/lib/env";
import { fetchUsername } from "@calcom/lib/fetchUsername";
import { pushGTMEvent } from "@calcom/lib/gtm";
import { useCompatSearchParams } from "@calcom/lib/hooks/useCompatSearchParams";
import { useDebounce } from "@calcom/lib/hooks/useDebounce";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { INVALID_CLOUDFLARE_TOKEN_ERROR } from "@calcom/lib/server/checkCfTurnstileToken";
import { IS_EUROPE } from "@calcom/lib/timezoneConstants";
import { signupSchema as apiSignupSchema } from "@calcom/prisma/zod-utils";
import type { inferSSRProps } from "@calcom/types/inferSSRProps";
import classNames from "@calcom/ui/classNames";
import { Alert } from "@calcom/ui/components/alert";
import { Button } from "@calcom/ui/components/button";
import { PasswordField, CheckboxField, TextField, Form, SelectField } from "@calcom/ui/components/form";
import { Icon } from "@calcom/ui/components/icon";
import { showToast } from "@calcom/ui/components/toast";

import type { getServerSideProps } from "@lib/signup/getServerSideProps";

import AuthContainer from "@components/ui/AuthContainer";

const signupSchema = apiSignupSchema.extend({
  apiError: z.string().optional(),
  cfToken: z.string().optional(),
});

const TurnstileCaptcha = dynamic(() => import("@calcom/web/modules/auth/components/Turnstile"), { ssr: false });

type FormValues = z.infer<typeof signupSchema>;

export type SignupProps = inferSSRProps<typeof getServerSideProps>;

function truncateDomain(domain: string) {
  const maxLength = 25;
  const cleanDomain = domain.replace(URL_PROTOCOL_REGEX, "");

  if (cleanDomain.length <= maxLength) {
    return cleanDomain;
  }

  return `${cleanDomain.substring(0, maxLength - 3)}.../`;
}

function UsernameField({
  username,
  setPremium,
  premium,
  setUsernameTaken,
  orgSlug,
  usernameTaken,
  disabled,
  ...props
}: React.ComponentProps<typeof TextField> & {
  username: string;
  setPremium: (value: boolean) => void;
  premium: boolean;
  usernameTaken: boolean;
  orgSlug?: string;
  setUsernameTaken: (value: boolean) => void;
}) {
  const { t } = useLocale();
  const { register, formState } = useFormContext<FormValues>();
  const debouncedUsername = useDebounce(username, 600);

  useEffect(() => {
    if (formState.isSubmitting || formState.isSubmitSuccessful) return;

    async function checkUsername() {
      if (disabled) return;
      if (!debouncedUsername) {
        setPremium(false);
        setUsernameTaken(false);
        return;
      }
      fetchUsername(debouncedUsername, orgSlug ?? null).then(({ data }) => {
        setPremium(data.premium);
        setUsernameTaken(!data.available);
      });
    }
    checkUsername();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUsername, disabled, orgSlug, formState.isSubmitting, formState.isSubmitSuccessful]);

  return (
    <div>
      <TextField
        disabled={disabled}
        {...props}
        {...register("username")}
        data-testid="signup-usernamefield"
      />
      {(!formState.isSubmitting || !formState.isSubmitted) && (
        <div className="text-gray text-default flex items-center text-sm">
          <div className="text-sm ">
            {usernameTaken ? (
              <div className="text-error flex items-center">
                <Icon name="info" className="mr-1 inline-block h-4 w-4" />
                <p>{t("already_in_use_error")}</p>
              </div>
            ) : premium ? (
              <div data-testid="premium-username-warning" className="flex items-center">
                <Icon name="star" className="mr-1 inline-block h-4 w-4" />
                <p>
                  {t("premium_username", {
                    price: getPremiumPlanPriceValue(),
                    interpolation: { escapeValue: false },
                  })}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function addOrUpdateQueryParam(url: string, key: string, value: string) {
  const separator = url.includes("?") ? "&" : "?";
  const param = `${key}=${encodeURIComponent(value)}`;
  return `${url}${separator}${param}`;
}

export default function Signup({
  prepopulateFormValues,
  token,
  orgSlug,
  isGoogleLoginEnabled,
  isSAMLLoginEnabled,
  orgAutoAcceptEmail,
  redirectUrl,
  emailVerificationEnabled,
  onboardingV3Enabled,
}: SignupProps) {
  const isOrgInviteByLink = orgSlug && !prepopulateFormValues?.username;
  const [isSamlSignup, setIsSamlSignup] = useState(false);
  const [regionSelected, setRegionSelected] = useState(false);
  const [premiumUsername, setPremiumUsername] = useState(false);
  const [usernameTaken, setUsernameTaken] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [displayEmailForm, setDisplayEmailForm] = useState(token);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const searchParams = useCompatSearchParams();
  const { t, i18n } = useLocale();
  const router = useRouter();

  useEffect(() => {
    const planParam = searchParams?.get("plan");
    if (planParam) {
      try { localStorage.setItem("schedly_signup_plan", planParam); } catch {}
    }
  }, [searchParams]);
  const formMethods = useForm<FormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: prepopulateFormValues satisfies FormValues,
    mode: "onChange",
  });
  const {
    register,
    watch,
    formState: { isSubmitting, errors, isSubmitSuccessful },
  } = formMethods;

  useEffect(() => {
    if (redirectUrl) {
      localStorage.setItem("onBoardingRedirect", redirectUrl);
    }
  }, [redirectUrl]);

  const [userConsentToCookie, setUserConsentToCookie] = useState(false);

  function handleConsentChange(consent: boolean) {
    setUserConsentToCookie(!consent);
  }

  const loadingSubmitState = isSubmitSuccessful || isSubmitting;
  // Show the back button whenever the user is past the first step
  const displayBackButton = token ? false : displayEmailForm || regionSelected;

  const isPlatformUser = redirectUrl?.includes("platform") && redirectUrl?.includes("new");

  const signUp: SubmitHandler<FormValues> = async (_data) => {
    const { cfToken, ...data } = _data;

    posthog.capture("signup_form_submitted", {
      has_token: !!token,
      is_org_invite: isOrgInviteByLink,
      org_slug: orgSlug,
      is_premium_username: premiumUsername,
      username_taken: usernameTaken,
    });

    try {
      const result = await fetchSignup(
        {
          ...data,
          language: i18n.language,
          token,
        } as Parameters<typeof fetchSignup>[0]
      );

      if (!result.ok) {
        if (isUserAlreadyExistsError(result)) {
          showToast(t("account_already_exists_please_login"), "warning");
          const callbackUrl = token ? `/teams?token=${token}` : "/event-types";
          setTimeout(() => {
            router.push(`/auth/login?callbackUrl=${encodeURIComponent(callbackUrl)}`);
          }, 3000);
          return;
        }

        if (hasCheckoutSession(result)) {
          const stripe = await getStripe();
          if (stripe) {
            const { error } = await stripe.redirectToCheckout({
              sessionId: result.error.checkoutSessionId,
            });
            if (error) console.warn(error.message);
          }
          return;
        }

        throw new Error(result.error.message);
      }

      if (process.env.NEXT_PUBLIC_GTM_ID) {
        pushGTMEvent("create_account", { email: data.email, user: data.username, lang: data.language });
      }

      const gettingStartedPath = onboardingV3Enabled ? "onboarding/getting-started" : "getting-started";
      const verifyOrGettingStarted = emailVerificationEnabled ? "auth/verify-email" : gettingStartedPath;
      const gettingStartedWithPlatform = "settings/platform/new";

      const constructCallBackIfUrlPresent = () => {
        if (isOrgInviteByLink) {
          return `${WEBAPP_URL}/${searchParams.get("callbackUrl")}`;
        }
        return addOrUpdateQueryParam(`${WEBAPP_URL}/${searchParams.get("callbackUrl")}`, "from", "signup");
      };

      const constructCallBackIfUrlNotPresent = () => {
        if (isPlatformUser) {
          return `${WEBAPP_URL}/${gettingStartedWithPlatform}?from=signup`;
        }
        return `${WEBAPP_URL}/${verifyOrGettingStarted}?from=signup`;
      };

      const constructCallBackUrl = () => {
        const callbackUrlSearchParams = searchParams?.get("callbackUrl");
        return callbackUrlSearchParams ? constructCallBackIfUrlPresent() : constructCallBackIfUrlNotPresent();
      };

      await signIn<"credentials">("credentials", {
        ...data,
        callbackUrl: constructCallBackUrl(),
      });
    } catch (err) {
      setTurnstileKey((k) => k + 1);
      formMethods.setValue("cfToken", undefined);

      const errorMessage = err instanceof Error ? err.message : t("unexpected_error_try_again");

      if (errorMessage === INVALID_CLOUDFLARE_TOKEN_ERROR) {
        return;
      }

      posthog.capture("signup_form_submit_error", {
        has_token: !!token,
        is_org_invite: isOrgInviteByLink,
        org_slug: orgSlug,
        is_premium_username: premiumUsername,
        error_message: errorMessage,
      });
      formMethods.setError("apiError", { message: errorMessage });
    }
  };

  const SignupFooter = (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
        <span style={{ color: "#64748b" }}>{t("already_have_account")}</span>
        <Link href="/auth/login" style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}>
          {t("sign_in")}
        </Link>
      </div>
      <div style={{ color: "#94a3b8", fontSize: 11, textAlign: "center" }}>
        <ServerTrans
          t={t}
          i18nKey="signing_up_terms"
          components={[
            <Link
              style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}
              key="terms"
              href={`${WEBSITE_TERMS_URL}`}
              target="_blank">
              Terms
            </Link>,
            <Link
              style={{ color: "#6366f1", fontWeight: 600, textDecoration: "none" }}
              key="privacy"
              href={`${WEBSITE_PRIVACY_POLICY_URL}`}
              target="_blank">
              Privacy Policy.
            </Link>,
          ]}
        />
      </div>
    </div>
  );

  return (
    <>
      {IS_CALCOM && (!IS_EUROPE || userConsentToCookie) ? (
        <>
          {process.env.NEXT_PUBLIC_GTM_ID && (
            <>
              <Script
                id="gtm-init-script"
                dangerouslySetInnerHTML={{
                  __html: `(function (w, d, s, l, i) {
                        w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
                        var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
                        j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f);
                    })(window, document, 'script', 'dataLayer', '${process.env.NEXT_PUBLIC_GTM_ID}');`,
                }}
              />
              <noscript
                dangerouslySetInnerHTML={{
                  __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
                }}
              />
            </>
          )}
          <DubAnalytics
            apiHost="/_proxy/dub"
            cookieOptions={{
              domain: isENVDev ? undefined : `.${new URL(WEBSITE_URL).hostname}`,
            }}
            domainsConfig={{
              refer: "refer.schedly.io",
            }}
          />
        </>
      ) : null}
      <AuthContainer
        showLogo
        visualVariant="signup"
        heading={IS_CALCOM ? t("create_your_calcom_account") : t("create_your_account")}
        subheading={IS_CALCOM ? t("cal_signup_description") : t("calcom_explained", { appName: APP_NAME })}
        footerText={SignupFooter}
        backButton={
          displayBackButton ? (
            <Button
              color="minimal"
              className="auth-back-btn"
              StartIcon="arrow-left"
              data-testid="signup-back-button"
              onClick={() => {
                if (displayEmailForm) {
                  setDisplayEmailForm(false);
                  setIsSamlSignup(false);
                } else if (regionSelected) {
                  setRegionSelected(false);
                }
              }}>
              {t("back")}
            </Button>
          ) : undefined
        }
      >
        {displayEmailForm && (
          <Form
            className="flex flex-col gap-4"
            form={formMethods}
            handleSubmit={async (values) => {
              let updatedValues = values;
              if (!formMethods.getValues().username && isOrgInviteByLink) {
                updatedValues = {
                  ...values,
                  username: getOrgUsernameFromEmail(values.email, orgAutoAcceptEmail),
                };
              }
              await signUp(updatedValues);
            }}>
            {!isOrgInviteByLink ? (
              <UsernameField
                orgSlug={orgSlug}
                label={t("username")}
                username={watch("username") || ""}
                premium={premiumUsername}
                usernameTaken={usernameTaken}
                disabled={!!orgSlug}
                setUsernameTaken={(value) => setUsernameTaken(value)}
                data-testid="signup-usernamefield"
                setPremium={(value) => setPremiumUsername(value)}
                addOnLeading={
                  orgSlug
                    ? truncateDomain(
                        `${getOrgFullOrigin(orgSlug, { protocol: true }).replace(
                          URL_PROTOCOL_REGEX,
                          ""
                        )}/`
                      )
                    : truncateDomain(
                        `${process.env.NEXT_PUBLIC_WEBSITE_URL.replace(URL_PROTOCOL_REGEX, "")}/`
                      )
                }
              />
            ) : null}
            <TextField
              id="signup-email"
              {...register("email")}
              label={t("email")}
              placeholder="john@doe.com"
              type="email"
              autoComplete="email"
              disabled={prepopulateFormValues?.email}
              data-testid="signup-emailfield"
            />

            {!isSamlSignup && (
              <PasswordField
                id="signup-password"
                data-testid="signup-passwordfield"
                autoComplete="new-password"
                label={t("password")}
                {...register("password")}
                hintErrors={["caplow", "min", "num"]}
              />
            )}
            <CheckboxField
              data-testid="signup-cookie-content-checkbox"
              onChange={() => handleConsentChange(userConsentToCookie)}
              description={t("cookie_consent_checkbox")}
            />
            {errors.apiError && (
              <Alert
                className="mb-3"
                severity="error"
                message={errors.apiError?.message}
                data-testid="signup-error-message"
              />
            )}
            {isSamlSignup ? (
              <Button
                data-testid="saml-submit-button"
                color="primary"
                disabled={
                  !!formMethods.formState.errors.username ||
                  !!formMethods.formState.errors.email ||
                  !formMethods.getValues("email") ||
                  !formMethods.getValues("username") ||
                  premiumUsername ||
                  isSubmitting
                }
                onClick={() => {
                  const username = formMethods.getValues("username");
                  if (!username) {
                    showToast(t("username_required"), "error");
                    return;
                  }

                  posthog.capture("signup_saml_submit_button_clicked", {
                    has_token: !!token,
                    is_org_invite: isOrgInviteByLink,
                    org_slug: orgSlug,
                  });

                  localStorage.setItem("username", username);
                  const sp = new URLSearchParams();
                  sp.set("username", username);
                  sp.set("email", formMethods.getValues("email"));
                  router.push(
                    `${process.env.NEXT_PUBLIC_WEBAPP_URL}/auth/sso/saml` + `?${sp.toString()}`
                  );
                }}
                className={classNames(
                  "w-full justify-center",
                  formMethods.formState.errors.username && formMethods.formState.errors.email
                    ? "opacity-50"
                    : ""
                )}>
                <Icon name="shield-check" className="mr-2 h-5 w-5" />
                {t("create_account_with_saml")}
              </Button>
            ) : (
              <Button
                type="submit"
                data-testid="signup-submit-button"
                className="w-full justify-center"
                loading={loadingSubmitState}
                disabled={
                  !!formMethods.formState.errors.username ||
                  !!formMethods.formState.errors.email ||
                  !formMethods.getValues("email") ||
                  !formMethods.getValues("password") ||
                  isSubmitting ||
                  usernameTaken
                }>
                {premiumUsername && !usernameTaken
                  ? `${t("get_started")} (${getPremiumPlanPriceValue()})`
                  : t("get_started")}
              </Button>
            )}
          </Form>
        )}
        {!regionSelected && !displayEmailForm && (
          <div className="flex flex-col gap-3">
            <p style={{ fontSize: 11, color: "#64748b", textAlign: "center", margin: "0 0 4px 0", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 }}>
              Select your data region to continue
            </p>
            <button
              type="button"
              className="auth-region-btn"
              onClick={() => {
                setRegionSelected(true);
              }}>
              <img src="/flag-us.png" alt="" className="auth-region-flag" />
              <span>{t("united_states")}</span>
            </button>
            <button
              type="button"
              className="auth-region-btn"
              onClick={() => {
                setRegionSelected(true);
              }}>
              <img src="/flag-eu.png" alt="" className="auth-region-flag" />
              <span>{t("european_union")}</span>
            </button>
          </div>
        )}

        {!displayEmailForm && regionSelected && (
          <div className="flex flex-col gap-4">
            {isGoogleLoginEnabled && (
              <>
                <Button
                  color="primary"
                  loading={isGoogleLoading}
                  CustomStartIcon={
                    <img
                      className={classNames(
                        "text-subtle mr-2 h-4 w-4",
                        premiumUsername && "opacity-50"
                      )}
                      src="/google-icon-colored.svg"
                      alt="Continue with Google Icon"
                    />
                  }
                  className="w-full justify-center"
                  data-testid="continue-with-google-button"
                  onClick={async () => {
                    posthog.capture("signup_google_button_clicked", {
                      has_token: !!token,
                      is_org_invite: isOrgInviteByLink,
                      org_slug: orgSlug,
                      has_prepopulated_username: !!prepopulateFormValues?.username,
                    });
                    setIsSamlSignup(false);
                    setIsGoogleLoading(true);
                    const baseUrl = process.env.NEXT_PUBLIC_WEBAPP_URL;
                    const GOOGLE_AUTH_URL = `${baseUrl}/auth/sso/google`;
                    const searchQueryParams = new URLSearchParams();
                    if (prepopulateFormValues?.username) {
                      searchQueryParams.set("username", prepopulateFormValues.username);
                      localStorage.setItem("username", prepopulateFormValues.username);
                    }
                    if (token) {
                      searchQueryParams.set("email", prepopulateFormValues?.email);
                    }
                    const url = searchQueryParams.toString()
                      ? `${GOOGLE_AUTH_URL}?${searchQueryParams.toString()}`
                      : GOOGLE_AUTH_URL;

                    router.push(url);
                  }}>
                  {t("continue_with_google")}
                </Button>

                <div className="flex items-center gap-4">
                  <div className="grow border-t" style={{ borderColor: "#e2e8f0" }} />
                  <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 500 }}>
                    {t("or").toLocaleLowerCase()}
                  </span>
                  <div className="grow border-t" style={{ borderColor: "#e2e8f0" }} />
                </div>
              </>
            )}

            <Button
              color="secondary"
              disabled={isGoogleLoading}
              className="w-full justify-center"
              onClick={() => {
                posthog.capture("signup_email_button_clicked", {
                  has_token: !!token,
                  is_org_invite: isOrgInviteByLink,
                  org_slug: orgSlug,
                });
                setDisplayEmailForm(true);
                setIsSamlSignup(false);
              }}
              data-testid="continue-with-email-button">
              {t("continue_with_email")}
            </Button>
            {isSAMLLoginEnabled && (
              <Button
                data-testid="continue-with-saml-button"
                color="minimal"
                disabled={isGoogleLoading}
                className="w-full justify-center"
                onClick={() => {
                  posthog.capture("signup_saml_button_clicked", {
                    has_token: !!token,
                    is_org_invite: isOrgInviteByLink,
                    org_slug: orgSlug,
                  });
                  setDisplayEmailForm(true);
                  setIsSamlSignup(true);
                }}>
                {`${t("or").toLocaleLowerCase()} ${t("saml_sso")}`}
              </Button>
            )}
          </div>
        )}
      </AuthContainer>
      <Toaster position="bottom-right" />
    </>
  );
}
